import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import type { Session } from "@event-planner/shared";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { SchemaField, type FieldConfig } from "../../components/forms/SchemaField";
import { AudiencePicker } from "../../components/AudiencePicker";
import { PdfUpload } from "../../components/uploads/PdfUpload";
import {
  LocationPicker,
  isPickerValueDirty,
  pickerValueStub,
  type LocationPickerValue,
} from "../../components/forms/LocationPicker";
import { useLocationSaveConfirm } from "../../components/forms/useLocationSaveConfirm";
import { useCreateSession, useUpdateSession } from "../../hooks/useSessionMutations";
import { useEvent } from "../../layouts/EventLayout";
import { useEventVenue } from "../../hooks/useEventVenue";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { sessionSchema, type SessionFormData } from "../../schemas/sessionSchema";
import {
  dateTimeLocalToTimestamp,
  timestampToDateTimeLocal,
} from "../../utils/timestamps";

function buildDefaultValues(
  initialData: Session | undefined,
  initialPickedAudienceIds: string[],
  timezone: string,
): SessionFormData {
  if (initialData) {
    return {
      title: initialData.title,
      description: initialData.description,
      sessionTypeId: initialData.sessionTypeId,
      pickedAudienceIds: initialPickedAudienceIds,
      startTime: timestampToDateTimeLocal(initialData.startTime, timezone),
      endTime: timestampToDateTimeLocal(initialData.endTime, timezone),
      locationId: initialData.locationId ?? null,
      materialsURL: initialData.materialsURL,
      materialsFileName: initialData.materialsFileName,
    };
  }

  return {
    title: "",
    description: null,
    sessionTypeId: "",
    pickedAudienceIds: [],
    startTime: "",
    endTime: "",
    locationId: null,
    materialsURL: null,
    materialsFileName: null,
  };
}

interface SessionFormProps {
  mode: "create" | "edit";
  initialData?: Session;
  showVisibilitySection?: boolean;
  /**
   * When provided, SessionForm reports its dirty/submitting state up to the
   * parent and skips installing its own navigation guard. The parent is
   * expected to install a single page-level guard — react-router only
   * allows one active blocker at a time per data router.
   */
  onDirtyChange?: (isDirty: boolean) => void;
  onSubmittingChange?: (isSubmitting: boolean) => void;
  /**
   * Audience IDs the form's picker should be pre-loaded with on edit.
   * The parent page resolves the session's `audienceId` into this list
   * (named audience → `[audienceId]`; anonymous composition →
   * `members.includedAudienceIds`).
   */
  initialPickedAudienceIds?: string[];
}

export function SessionForm({
  mode,
  initialData,
  showVisibilitySection = true,
  onDirtyChange,
  onSubmittingChange,
  initialPickedAudienceIds = [],
}: SessionFormProps) {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: creating } = useCreateSession();
  const { update, loading: updating } = useUpdateSession();
  const [serverError, setServerError] = useState<string | null>(null);

  const timezone = event?.timezone ?? "UTC";
  const sessionTypes = event?.config.sessionTypes ?? [];

  const form = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: buildDefaultValues(
      initialData,
      initialPickedAudienceIds,
      timezone,
    ),
  });

  const selectedTypeId = form.watch("sessionTypeId");
  const selectedType = sessionTypes.find((st) => st.id === selectedTypeId);
  const submitting = creating || updating;

  // Create mode with initialData = duplicating an existing session
  // (SessionCreatePage hydrates from `?from=<sessionId>`). The form is
  // pre-filled and the source's speakers/materials are carried through.
  const isDuplicate = mode === "create" && !!initialData;

  // Two modes:
  //   - Standalone (SessionCreatePage): SessionForm owns its own guard.
  //   - Embedded (SessionEditPage tabs): parent owns the guard so a single
  //     blocker covers all tabs. We feed the internal guard a no-op
  //     predicate (active=false) and report state up via callbacks.
  const externallyManaged = !!onDirtyChange;
  const isDirty = form.formState.isDirty;
  const isSubmitting = form.formState.isSubmitting || submitting;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);
  useEffect(() => {
    onSubmittingChange?.(isSubmitting);
  }, [isSubmitting, onSubmittingChange]);

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    externallyManaged ? false : isDirty,
    externallyManaged ? true : isSubmitting,
  );

  // On create, when the user picks (or changes) a category, pre-fill
  // pickedAudienceIds with the category's defaultAudienceId (if any).
  // ADR-120 invariant: one-way create-only inheritance; once a session is
  // saved, edits to the category config never re-pull the default. The
  // user can freely add/remove audience picks afterward.
  const lastTypeIdRef = useRef<string>(initialData?.sessionTypeId ?? "");
  useEffect(() => {
    if (mode !== "create") return;
    if (!selectedTypeId) return;
    if (selectedTypeId === lastTypeIdRef.current) return;
    lastTypeIdRef.current = selectedTypeId;
    const type = sessionTypes.find((st) => st.id === selectedTypeId);
    if (type) {
      form.setValue(
        "pickedAudienceIds",
        type.defaultAudienceId ? [type.defaultAudienceId] : [],
        { shouldDirty: true },
      );
    }
  }, [selectedTypeId, sessionTypes, form, mode]);

  const pickerDefaultColor = selectedType?.backgroundColor ?? "#388E3C";
  const [pickerValue, setPickerValue] = useState<LocationPickerValue>(
    initialData?.locationId
      ? pickerValueStub(initialData.locationId, pickerDefaultColor)
      : null,
  );

  // Form-level dirty gate for the LocationPicker. See EventForm for
  // the architecture rationale — same pattern, same reasons.
  const baselineRef = useRef<LocationPickerValue>(
    initialData?.locationId
      ? pickerValueStub(initialData.locationId, pickerDefaultColor)
      : null,
  );
  useEffect(() => {
    const baseline = baselineRef.current;
    if (
      baseline?.locationId &&
      baseline.locationId === pickerValue?.locationId &&
      !baseline.label &&
      pickerValue?.label
    ) {
      baselineRef.current = pickerValue;
    }
  }, [pickerValue]);
  const { venue: eventVenue } = useEventVenue(event);
  const pickerBiasCoordinate = eventVenue?.coordinates ?? undefined;

  const sessionTypeOptions = useMemo(
    () => sessionTypes.map((st) => ({ value: st.id, label: st.name })),
    [sessionTypes],
  );
  const selectedAudienceIds = form.watch("pickedAudienceIds") ?? [];

  const basicFields: FieldConfig<SessionFormData>[] = [
    { name: "title", label: "Title", type: "text", required: true },
    { name: "description", label: "Description", type: "textarea", rows: 3 },
  ];

  const typeFields: FieldConfig<SessionFormData>[] = [
    {
      name: "sessionTypeId",
      label: "Category",
      type: "select",
      required: true,
      options: sessionTypeOptions,
    },
  ];

  const scheduleFields: FieldConfig<SessionFormData>[] = [
    {
      name: "startTime",
      label: `Start Time (${timezone})`,
      type: "datetime-local",
      required: true,
    },
    {
      name: "endTime",
      label: `End Time (${timezone})`,
      type: "datetime-local",
      required: true,
    },
  ];

  const { confirmAndResolve, dialog: locationSaveConfirmDialog } =
    useLocationSaveConfirm();

  async function onSubmit(data: SessionFormData) {
    setServerError(null);
    try {
      const startTime = dateTimeLocalToTimestamp(data.startTime, timezone);
      const endTime = dateTimeLocalToTimestamp(data.endTime, timezone);

      let locationId: string | null;
      if (
        initialData &&
        !isPickerValueDirty(baselineRef.current, pickerValue)
      ) {
        // Picker untouched since hydrate (edit mode OR duplicate mode —
        // both seed the picker from an existing session's location) —
        // preserve the original locationId verbatim, no resolver call,
        // no dialog, no doc write. Without this, saving a duplicate
        // with its carried location raises a spurious "Save changes to
        // existing location?" dialog for a location nobody edited.
        locationId = initialData.locationId ?? null;
      } else {
        const resolved = await confirmAndResolve(pickerValue, eventId!);
        if (resolved === undefined) return;
        locationId = resolved;
      }

      if (mode === "create") {
        const newSessionId = await create({
          eventId: eventId!,
          title: data.title,
          description: data.description,
          sessionTypeId: data.sessionTypeId,
          pickedAudienceIds: data.pickedAudienceIds,
          startTime,
          endTime,
          locationId,
          materialsURL: data.materialsURL,
          materialsFileName: data.materialsFileName,
          // Plain create: speakers are linked from the Speakers tab after
          // the session is created (ADR-136 amendment); start empty.
          // Duplicate: carry the source's speakers — onSessionWrite
          // derives speakerNames from them.
          speakerIds: initialData?.speakerIds ?? [],
        });
        showSuccess(
          isDuplicate
            ? "Session duplicated — speakers and materials copied"
            : "Session created — add speakers and attendees now",
        );
        navigate(`/events/${eventId}/agenda/${newSessionId}`);
        return;
      } else {
        await update(initialData!.id, eventId!, {
          title: data.title,
          description: data.description,
          sessionTypeId: data.sessionTypeId,
          ...(showVisibilitySection
            ? { pickedAudienceIds: data.pickedAudienceIds }
            : {}),
          startTime,
          endTime,
          locationId,
          materialsURL: data.materialsURL,
          materialsFileName: data.materialsFileName,
        });
        showSuccess("Session updated");
      }
      navigate(`/events/${eventId}/agenda`);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to save session";
      setServerError(message);
      showError(message);
    }
  }

  return (
    <>
      {locationSaveConfirmDialog}
      {unsavedGuardDialog}
      <FormPageLayout
        title={
          mode === "create" ? (isDuplicate ? "Duplicate Session" : "Create Session") : ""
        }
        onSubmit={form.handleSubmit(onSubmit)}
        error={serverError}
        submitting={submitting}
        submitLabel={mode === "create" ? "Create Session" : "Save Changes"}
        testIdSlug="session"
      >
        <FormSection title="Basic Info">
          {basicFields.map((field) => (
            <SchemaField key={field.name} config={field} control={form.control} />
          ))}
        </FormSection>

        <FormSection title="Category">
          {typeFields.map((field) => (
            <SchemaField key={field.name} config={field} control={form.control} />
          ))}
          {selectedType && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  backgroundColor: selectedType.backgroundColor,
                  flexShrink: 0,
                }}
              />
              <Typography variant="body2" color="text.secondary">
                {mode === "create"
                  ? selectedType.defaultAudienceId
                    ? "Visibility pre-fills from this category."
                    : "This category has no default visibility — pick groups or people below."
                  : "Category colors and defaults are set in Agenda > Categories."}
              </Typography>
            </Box>
          )}
        </FormSection>

        {showVisibilitySection && (
          <FormSection title="Visibility">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Pick the groups and people who should see this session.
              Visibility is explicit: nobody sees the session unless they're
              in the selected group or person list.
            </Typography>
            {selectedAudienceIds.length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No attendees will see this session until you pick at least
                one group or person.
              </Alert>
            )}
            <Controller
              control={form.control}
              name="pickedAudienceIds"
              render={({ field }) => (
                <AudiencePicker
                  eventId={eventId}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  label="Visible to"
                  placeholder="Pick groups or people…"
                  includePendingContacts
                  testId="session-audience"
                  required
                />
              )}
            />
          </FormSection>
        )}

        <FormSection title="Schedule">
          {scheduleFields.map((field) => (
            <SchemaField key={field.name} config={field} control={form.control} />
          ))}
        </FormSection>

        <FormSection title="Map Location">
          <LocationPicker
            eventId={eventId!}
            value={pickerValue}
            onChange={setPickerValue}
            defaultColor={pickerDefaultColor}
            testIdSuffix="session"
            mapHeight={180}
            searchBiasCoordinate={pickerBiasCoordinate}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            Pick an existing location, search for a venue, or type a label
            (toggle off) for a room name without a map pin.
          </Typography>
        </FormSection>

        {/* Rendered in edit mode AND duplicate mode (create + initialData)
            so a carried PDF is visible/removable/replaceable pre-save.
            In duplicate mode `storagePathPrefix` is the SOURCE session's
            folder — safe: per-session Storage objects are never deleted
            (no cleanup path exists) and the doc stores the URL, not the
            path, so the copy's reference cannot be stranded. */}
        {initialData && (
          <FormSection title="Materials">
            <Controller
              control={form.control}
              name="materialsURL"
              render={({ field: urlField }) => (
                <Controller
                  control={form.control}
                  name="materialsFileName"
                  render={({ field: nameField }) => (
                    <PdfUpload
                      value={
                        urlField.value && nameField.value
                          ? { url: urlField.value, fileName: nameField.value }
                          : null
                      }
                      onChange={(next) => {
                        urlField.onChange(next?.url ?? null);
                        nameField.onChange(next?.fileName ?? null);
                      }}
                      storagePathPrefix={`events/${eventId}/sessions/${initialData.id}/materials`}
                      label="Session PDF"
                    />
                  )}
                />
              )}
            />
          </FormSection>
        )}
      </FormPageLayout>
    </>
  );
}
